import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { CustomDatePicker } from "../../components/UI/CustomDatePicker";
import Loader from "../../components/Loader";
import useGetAppointmentDetail from "../../hooks/useGetAppointmentDetail";
import useUpdateAppointmentMutation from "../../hooks/useUpdateAppointmentMutation";
import useGetClinics from "../../hooks/useGetClinics";
import useGetPractitioners from "../../hooks/useGetPractitioners";
import useGetTreatmentTypesOld from "../../hooks/useGetTreatmentTypesOld";
import useGetTreatmentAreasOld from "../../hooks/useGetTreatmentAreasOld";

const UpdateAppointment = () => {
  const { _id_, UpdateId } = useParams();
  const navigate = useNavigate();

  const { setActive, setBreadcrumbTitle, isTrialLocked } = useOutletContext();
  setActive("appointments");
  setBreadcrumbTitle("Appointment Update");

  const { data: appointmentDetail, isLoading } = useGetAppointmentDetail({
    appointmentId: UpdateId,
  });
  const updateAppointmentMutation = useUpdateAppointmentMutation();
  const { data: ClinicList } = useGetClinics();
  const { data: treatmentTypes = [] } = useGetTreatmentTypesOld();
  const { data: treatmentAreas } = useGetTreatmentAreasOld();
  const treatmentAreasNeuro = treatmentAreas?.injectionList || [];
  const treatmentAreasFillers = treatmentAreas?.fillerList || [];
  const treatmentDrugsNeuro = treatmentTypes[0]?.options || [];
  const treatmentDrugsFillers = treatmentTypes[1]?.options || [];

  const [clinicId, setClinicId] = useState(null);
  const { data: PractitionerList } = useGetPractitioners({ clinicId, enabled: Boolean(clinicId) });
  const [dateTime, changeDateTime] = useState("");

  const [Appointment, setAppointmentData] = useState({
    duration: "",
    notes: "",
    status: "",
    practitioner: "",
    clinic: "",
    notifypush: false,
    notifysms: false,
    notifyemail: false,
    appointment_type_list: [],
  });

  var sampleAppoinment = {
    dosage: 0,
    dilution: 0,
    appointment_type: "",
    treatment_area: "",
    treatment_type: "",
    other_drugs: "",
  };

  useEffect(() => {
    if (!appointmentDetail) return;

    changeDateTime(appointmentDetail.time + "Z");
    setAppointmentData((Appointment) => ({
      ...Appointment,
      duration: appointmentDetail.duration,
      notes: appointmentDetail.notes,
      clinic: appointmentDetail.clinic_dict.id,
      practitioner: appointmentDetail.practitioner,
      status: appointmentDetail.status,
      appointment_type_list:
        appointmentDetail.appointment_type_list.length === 0
          ? [{ ...sampleAppoinment, appointment_type: 2 }]
          : appointmentDetail.appointment_type_list,
    }));
    setClinicId(appointmentDetail.clinic_dict.id);
  }, [appointmentDetail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAppointmentData((Appointment) => ({
      ...Appointment,
      [name]: value,
    }));
    if (name === "clinic") setClinicId(value);
  };

  const handleList = (e, rowIndex) => {
    const { name, value } = e.target;
    let updatedObj = { [name]: value };
    if (name === "appointment_type") updatedObj = { ...sampleAppoinment, ...updatedObj };
    setAppointmentData((Appointment) => ({
      ...Appointment,
      appointment_type_list: [
        ...Appointment.appointment_type_list.map((data, index) => {
          if (index === rowIndex) {
            return {
              ...data,
              ...updatedObj,
            };
          } else {
            return data;
          }
        }),
      ],
    }));
  };

  const UpdateInstance = (e) => {
    e.preventDefault();
    updateAppointmentMutation.mutate(
      { appointmentId: UpdateId, data: { ...Appointment, time: dateTime } },
      {
        onSuccess: () => {
          navigate(`/patient/${_id_}/appointment/`);
        },
      }
    );
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Update Appointment
      </Typography>

      {isLoading ? (
        <Loader />
      ) : (
        <form onSubmit={UpdateInstance}>
          {/* Schedule section */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Schedule
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <CustomDatePicker
              label="Appointment Time"
              value={dateTime}
              onChange={changeDateTime}
              variant="outlined"
              minutesStep={15}
            />
            <TextField
              label="Duration (minutes)"
              type="number"
              size="small"
              slotProps={{
                htmlInput: { step: 15, min: 15, max: 60 },
              }}
              value={Appointment.duration}
              name="duration"
              onChange={handleChange}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Assignment section */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Assignment
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
              gap: 2,
            }}
          >
            <FormControl size="small" required>
              <InputLabel>Clinic</InputLabel>
              <Select
                label="Clinic"
                name="clinic"
                onChange={handleChange}
                value={Appointment.clinic}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {ClinicList &&
                  ClinicList.map((data) => (
                    <MenuItem value={data.id} key={data.id}>
                      {data.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl size="small" required>
              <InputLabel>Practitioner</InputLabel>
              <Select
                label="Practitioner"
                value={Appointment.practitioner}
                name="practitioner"
                onChange={handleChange}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {PractitionerList &&
                  PractitionerList.map((data) =>
                    data.practitioner_clinic[0]?.id !== "" ? (
                      <MenuItem value={data.id} key={data.id}>
                        {data.name}
                      </MenuItem>
                    ) : null
                  )}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                name="status"
                onChange={handleChange}
                value={Appointment.status}
              >
                {appointmentDetail?.status !== "REQUESTED" && (
                  <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                )}
                {appointmentDetail?.status !== "COMPLETED" && (
                  <>
                    {appointmentDetail?.status === "REQUESTED" && (
                      <MenuItem value="REQUESTED">REQUESTED</MenuItem>
                    )}
                    <MenuItem value="UPCOMING">UPCOMING</MenuItem>
                    <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                  </>
                )}
              </Select>
            </FormControl>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Notes */}
          <TextField
            label="Notes"
            fullWidth
            size="small"
            multiline
            rows={2}
            value={Appointment.notes}
            name="notes"
            onChange={handleChange}
            placeholder="Enter notes here..."
          />

          <Divider sx={{ my: 3 }} />

          {/* Appointment Types */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Appointment Types
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setAppointmentData({
                  ...Appointment,
                  appointment_type_list: [...Appointment.appointment_type_list, sampleAppoinment],
                })
              }
            >
              Add Type
            </Button>
          </Box>

          <Stack spacing={1.5}>
            {Appointment.appointment_type_list.map((AppointmentType, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Appointment Type</InputLabel>
                    <Select
                      label="Appointment Type"
                      name="appointment_type"
                      value={AppointmentType.appointment_type || ""}
                      onChange={(e) => handleList(e, index)}
                    >
                      <MenuItem value="">
                        <em>Select</em>
                      </MenuItem>
                      {treatmentTypes.map((data) => (
                        <MenuItem value={data.id} key={data.id}>
                          {data.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box sx={{ ml: "auto" }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setAppointmentData({
                          ...Appointment,
                          appointment_type_list: Appointment.appointment_type_list.filter(
                            ({ appointment_type_row_id }) =>
                              appointment_type_row_id != AppointmentType.appointment_type_row_id
                          ),
                        });
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Treatment details shown when COMPLETED */}
                {Appointment.status === "COMPLETED" && AppointmentType.appointment_type !== 4 && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "1fr 1fr 1fr 1fr",
                      },
                      gap: 2,
                      mt: 2,
                      pt: 2,
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    <FormControl size="small">
                      <InputLabel>Treatment Area</InputLabel>
                      <Select
                        label="Treatment Area"
                        name="treatment_areas"
                        value={AppointmentType.treatment_areas || ""}
                        onChange={(e) => handleList(e, index)}
                      >
                        <MenuItem value="">
                          <em>Select</em>
                        </MenuItem>
                        {AppointmentType.appointment_type === 2 &&
                          treatmentAreasNeuro.map((data) => (
                            <MenuItem value={data.id} key={data.id}>
                              {data.name}
                            </MenuItem>
                          ))}
                        {AppointmentType.appointment_type === 3 &&
                          treatmentAreasFillers.map((data) => (
                            <MenuItem value={data.id} key={data.id}>
                              {data.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small">
                      <InputLabel>Treatment Type</InputLabel>
                      <Select
                        label="Treatment Type"
                        name="treatment_type"
                        value={AppointmentType.treatment_type || ""}
                        onChange={(e) => handleList(e, index)}
                      >
                        <MenuItem value="">
                          <em>Select</em>
                        </MenuItem>
                        {AppointmentType.appointment_type === 2 &&
                          treatmentDrugsNeuro.map((data) => (
                            <MenuItem value={data.id} key={data.id}>
                              {data.title}
                            </MenuItem>
                          ))}
                        {AppointmentType.appointment_type === 3 &&
                          treatmentDrugsFillers.map((data) => (
                            <MenuItem value={data.id} key={data.id}>
                              {data.title}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Dosage"
                      type="number"
                      size="small"
                      name="dosage"
                      value={AppointmentType.dosage || ""}
                      onChange={(e) => handleList(e, index)}
                    />
                    <TextField
                      label="Dilution"
                      type="number"
                      size="small"
                      name="dilution"
                      value={AppointmentType.dilution || ""}
                      onChange={(e) => handleList(e, index)}
                    />
                  </Box>
                )}
              </Paper>
            ))}
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* Notifications */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Notifications
          </Typography>
          <Stack direction="row" spacing={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Appointment.notifyemail}
                  onChange={() =>
                    setAppointmentData((prev) => ({
                      ...prev,
                      notifyemail: !prev.notifyemail,
                    }))
                  }
                />
              }
              label="Email"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Appointment.notifypush}
                  onChange={() =>
                    setAppointmentData((prev) => ({
                      ...prev,
                      notifypush: !prev.notifypush,
                    }))
                  }
                />
              }
              label="Push"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Appointment.notifysms}
                  onChange={() =>
                    setAppointmentData((prev) => ({
                      ...prev,
                      notifysms: !prev.notifysms,
                    }))
                  }
                />
              }
              label="SMS"
            />
          </Stack>

          {/* Actions */}
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 4 }}>
            <Button variant="outlined" component={Link} to={`/patient/${_id_}/appointment/`}>
              Cancel
            </Button>
            {(appointmentDetail?.status !== "REQUESTED" || Appointment.status !== "REQUESTED") && (
              <Button variant="contained" type="submit" disabled={isTrialLocked}>
                Update Appointment
              </Button>
            )}
          </Stack>
        </form>
      )}
    </Paper>
  );
};

export default UpdateAppointment;
